import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";

export type SheetStage = "collapsed" | "half" | "full";

interface Props {
  stage: SheetStage;
  onStageChange: (stage: SheetStage) => void;
  children: ReactNode;
}

const COLLAPSED_HEIGHT = 120;
const HANDLE_HEIGHT = 44;

export default function BizBottomSheet({ stage, onStageChange, children }: Props) {
  const [vh, setVh] = useState(() => (typeof window !== "undefined" ? window.innerHeight : 800));
  const [dragHeight, setDragHeight] = useState<number | null>(null);
  const dragStart = useRef({ y: 0, h: 0 });
  const movedRef = useRef(false);
  const draggingRef = useRef(false);

  useEffect(() => {
    const onResize = () => setVh(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const heights: Record<SheetStage, number> = {
    collapsed: COLLAPSED_HEIGHT,
    half: Math.max(COLLAPSED_HEIGHT, Math.round(vh * 0.5)),
    full: Math.max(COLLAPSED_HEIGHT, Math.round(vh * 0.9)),
  };

  const height = dragHeight ?? heights[stage];

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    draggingRef.current = true;
    movedRef.current = false;
    dragStart.current = { y: e.clientY, h: height };
    setDragHeight(height);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return;
    const delta = dragStart.current.y - e.clientY;
    if (Math.abs(delta) > 4) movedRef.current = true;
    const next = Math.min(heights.full, Math.max(heights.collapsed, dragStart.current.h + delta));
    setDragHeight(next);
  };

  const finishDrag = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const current = dragHeight ?? height;
    const stages: { key: SheetStage; value: number }[] = [
      { key: "collapsed", value: heights.collapsed },
      { key: "half", value: heights.half },
      { key: "full", value: heights.full },
    ];
    const nearest = stages.reduce((a, b) =>
      Math.abs(b.value - current) < Math.abs(a.value - current) ? b : a
    );
    setDragHeight(null);
    onStageChange(nearest.key);
  };

  const onHandleClick = () => {
    if (movedRef.current) return;
    const order: SheetStage[] = ["collapsed", "half", "full"];
    const idx = order.indexOf(stage);
    onStageChange(order[(idx + 1) % order.length]);
  };

  return (
    <div
      className="flex flex-col rounded-t-2xl border border-background-200 bg-background-50 shadow-[0_-6px_24px_rgba(0,0,0,0.10)]"
      style={{
        height: `${height}px`,
        transition: dragHeight == null ? "height 260ms cubic-bezier(0.22,1,0.36,1)" : "none",
      }}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label="목록 패널 높이 조절"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finishDrag}
        onPointerCancel={finishDrag}
        onClick={onHandleClick}
        className="flex cursor-grab touch-none select-none items-center justify-center active:cursor-grabbing"
        style={{ height: `${HANDLE_HEIGHT}px` }}
      >
        <span className="h-1.5 w-10 rounded-full bg-background-300" />
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
    </div>
  );
}